import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import {
  Check, ArrowRight, Heart, Brain, Users, Sparkles,
  Star, Zap, Shield, MessageCircle, Building2, TrendingUp, Handshake,
} from 'lucide-react';

// ── Planos ────────────────────────────────────────────────────────────────────

const PLANS_B2C = [
  {
    id: 'free',
    name: 'Free',
    badge: null,
    price: null,
    priceLabel: 'Grátis',
    priceSub: 'para sempre',
    description: 'Comece sua jornada sem custo.',
    features: [
      'Acesso às ilhas 1, 2 e 3 do MVP',
      'Exercícios interativos das trilhas iniciais',
      'Sistema de gamificação com XP',
      'Diário de emoções',
    ],
    cta: 'Começar grátis',
    popular: false,
    highlight: false,
  },
  {
    id: 'essencial',
    name: 'Plano Essencial',
    badge: 'Mais escolhido',
    price: { monthly: 39.90, annual: 297.00 },
    priceLabel: null,
    description: 'Para quem quer transformar de verdade suas relações.',
    features: [
      'Acesso completo às trilhas personalizadas por IA',
      'Sistema de gamificação com acúmulo de XP',
      'Acompanhamento e feedback personalizado de IA',
      'Relatório de evolução periódico',
      'Compra de horas de mentoria no app',
      'Acompanhamento por especialista',
    ],
    cta: 'Assinar agora',
    popular: true,
    highlight: true,
  },
];

const PLANS_B2B = [
  {
    id: 'business',
    name: 'Business',
    subtitle: 'Smart Culture',
    icon: <Building2 className="w-6 h-6" />,
    target: 'Empresas de pequeno e médio porte (até 300 funcionários)',
    priceLabel: 'R$ 3.000',
    priceSub: '/mês · até 100 vidas',
    priceExtra: '+ R$ 25/vida adicional',
    features: [
      'Acesso à plataforma para todos os colaboradores',
      'Dashboard de RH (Clima Organizacional, NPS e métricas NR1)',
      'Suporte de um Gestor de Contas dedicado',
    ],
    cta: 'Falar com Especialista',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    subtitle: 'Human Analytics + Mentoria',
    icon: <TrendingUp className="w-6 h-6" />,
    target: 'Grandes corporações que buscam impacto profundo',
    priceLabel: 'R$ 15 – R$ 20',
    priceSub: '/vida/mês',
    priceExtra: '+ Taxa de Mentoria: R$ 5.000/mês',
    features: [
      'Tudo do Plano Business',
      'Mentorias mensais ao vivo para lideranças',
      'Relatórios customizados e situacionais',
      'Análise de conflitos e qualidade das relações',
    ],
    cta: 'Falar com Especialista',
    featured: true,
  },
  {
    id: 'especialista',
    name: 'Especialista',
    subtitle: 'Parcerias',
    icon: <Handshake className="w-6 h-6" />,
    target: 'Palestrantes e consultores',
    priceLabel: 'Revenue Share',
    priceSub: 'participação nos lucros',
    priceExtra: null,
    features: [
      'Crie trilhas próprias dentro do app',
      'Comissões diferenciadas para vendas corporativas',
      'Continuidade das suas palestras e consultorias',
    ],
    cta: 'Quero ser parceiro',
  },
];

// ── Componentes auxiliares ────────────────────────────────────────────────────

const FeatureItem = ({ text, dark = false }) => (
  <li className="flex items-start gap-3">
    <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${dark ? 'text-purple-300' : 'text-purple-500'}`} />
    <span className={`text-sm leading-relaxed ${dark ? 'text-white/80' : 'text-gray-600'}`}>{text}</span>
  </li>
);

// ── Landing Page ──────────────────────────────────────────────────────────────

const LandingPage = () => {
  const navigate = useNavigate();
  const [billing, setBilling] = useState('annual'); // 'monthly' | 'annual'

  return (
    <div className="min-h-screen overflow-hidden">

      {/* ══════════════════════════════════════════════════
          SEÇÃO 1 — HERO
      ══════════════════════════════════════════════════ */}
      <section className="relative bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 text-white">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-20 left-10 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />
          <div className="absolute top-40 right-10 w-80 h-80 bg-indigo-500/20 rounded-full blur-3xl" />
        </div>

        {/* Nav */}
        <nav className="relative z-50 py-5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
            <img
              src="/logo-simby-white.png"
              alt="A Simby"
              style={{ width: 100 }}
              onError={e => { e.target.style.display = 'none'; }}
            />
            <div className="flex items-center gap-3">
              <Button variant="ghost" className="text-white/80 hover:text-white hover:bg-white/10" onClick={() => navigate('/login')}>
                Entrar
              </Button>
              <Button className="bg-white text-purple-700 hover:bg-purple-50 shadow-lg" onClick={() => navigate('/login')}>
                Começar Agora
              </Button>
            </div>
          </div>
        </nav>

        {/* Hero content */}
        <div className="relative pt-12 pb-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">

              {/* Texto */}
              <div className="space-y-7">
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md text-purple-200 px-4 py-2 rounded-full text-sm font-medium border border-white/20">
                  <Sparkles className="w-4 h-4 text-yellow-400" />
                  <span>Inteligência Relacional com IA</span>
                </div>

                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
                  Construa relações
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-pink-300 via-purple-300 to-indigo-300"> mais humanas</span>
                </h1>

                <p className="text-lg text-white/80 leading-relaxed max-w-xl">
                  A Simby foi criada para desenvolver inteligência relacional: comunicar melhor, lidar com conflitos, construir relações com mais presença.
                  O conteúdo é baseado em uma metodologia de 28 habilidades relacionais e em lições interativas com IA.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 pt-2">
                  <Button
                    size="lg"
                    className="bg-white text-purple-700 hover:bg-purple-50 px-8 py-6 text-lg rounded-2xl shadow-xl transition-all duration-300 hover:scale-105"
                    onClick={() => navigate('/login')}
                  >
                    Comece gratuitamente
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg rounded-2xl"
                    onClick={() => document.getElementById('planos')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    Ver planos
                  </Button>
                </div>

                <div className="flex gap-10 pt-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold">28</div>
                    <div className="text-xs text-white/60 mt-1">Habilidades</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold">5</div>
                    <div className="text-xs text-white/60 mt-1">Trilhas</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold">∞</div>
                    <div className="text-xs text-white/60 mt-1">Simulações IA</div>
                  </div>
                </div>
              </div>

              {/* Mascote */}
              <div className="relative flex justify-center lg:justify-end">
                <div className="relative">
                  <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 shadow-2xl">
                    <div className="bg-gradient-to-br from-purple-500/30 to-pink-500/30 rounded-2xl p-6">
                      <img src="/images/simby-mascote.png" alt="A Simby" className="w-56 h-72 object-contain mx-auto drop-shadow-2xl" />
                    </div>
                    <div className="mt-6 text-center">
                      <p className="text-lg font-medium text-white">Olá! Eu sou a Simby 🦒</p>
                      <p className="text-sm text-white/70 mt-1">Sua companheira na jornada relacional</p>
                    </div>
                  </div>
                  <div className="absolute -top-4 -right-4 bg-gradient-to-r from-green-400 to-emerald-500 text-white px-4 py-2 rounded-xl text-sm font-medium shadow-lg flex items-center gap-1">
                    <Star className="w-4 h-4 fill-white" /> 4.9/5
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════
          SEÇÃO 2 — O QUE A SIMBY OFERECE
      ══════════════════════════════════════════════════ */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Por que escolher <span className="text-purple-600">A Simby</span>?
            </h2>
            <p className="text-xl text-gray-500 max-w-2xl mx-auto">
              Uma metodologia completa, entregue com tecnologia e presença.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: <Heart className="w-7 h-7" />,
                title: 'Trilhas personalizadas',
                description: 'Jornada adaptada ao seu perfil relacional — com 28 habilidades mapeadas.',
              },
              {
                icon: <Brain className="w-7 h-7" />,
                title: 'Exercícios interativos',
                description: 'Pratique com situações reais do dia a dia e receba feedback imediato.',
              },
              {
                icon: <Users className="w-7 h-7" />,
                title: 'Simulações com IA',
                description: 'Converse com a IA em cenários realistas — conflitos, pedidos, empatia.',
              },
              {
                icon: <Sparkles className="w-7 h-7" />,
                title: 'Gamificação real',
                description: 'XP, conquistas, streaks e uma acácia que cresce com sua prática.',
              },
            ].map((f, i) => (
              <Card key={i} className="group bg-gray-50 border border-gray-100 hover:border-purple-200 p-6 rounded-2xl transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
                <div className="w-14 h-14 rounded-2xl bg-purple-100 flex items-center justify-center mb-5 group-hover:bg-purple-200 transition-all text-purple-600">
                  {f.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{f.title}</h3>
                <p className="text-gray-500 leading-relaxed text-sm">{f.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════
          SEÇÃO 3 — PLANOS INDIVIDUAIS (B2C)
      ══════════════════════════════════════════════════ */}
      <section id="planos" className="py-24 bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <p className="text-xs font-bold uppercase tracking-widest text-purple-300 mb-3">Para pessoas</p>
            <h2 className="text-4xl md:text-5xl font-bold mb-3">
              Comece sua <span className="text-pink-300">jornada</span>
            </h2>
            <p className="text-white/60 max-w-xl mx-auto">
              Escolha o plano que faz mais sentido para você agora.
            </p>
          </div>

          {/* Toggle mensal/anual */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex items-center gap-1 bg-white/10 rounded-xl p-1">
              <button
                onClick={() => setBilling('monthly')}
                className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${billing === 'monthly' ? 'bg-white text-purple-700 shadow' : 'text-white/60 hover:text-white'}`}
              >
                Mensal
              </button>
              <button
                onClick={() => setBilling('annual')}
                className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${billing === 'annual' ? 'bg-white text-purple-700 shadow' : 'text-white/60 hover:text-white'}`}
              >
                Anual
                <span className="bg-green-400 text-white text-xs px-2 py-0.5 rounded-full">-38%</span>
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {PLANS_B2C.map((plan) => {
              const price = plan.price
                ? billing === 'annual'
                  ? { value: plan.price.annual, label: '/ano', note: '12x de R$ ' + (plan.price.annual / 12).toFixed(2).replace('.', ',') }
                  : { value: plan.price.monthly, label: '/mês', note: null }
                : null;

              return (
                <Card
                  key={plan.id}
                  className={`relative p-8 rounded-3xl transition-all duration-300 hover:scale-[1.02] ${
                    plan.highlight
                      ? 'bg-white text-gray-900 border-4 border-purple-300 shadow-2xl'
                      : 'bg-white/10 backdrop-blur-sm border border-white/20 text-white'
                  }`}
                >
                  {plan.badge && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-1.5 rounded-full text-xs font-semibold shadow-lg whitespace-nowrap">
                      ⭐ {plan.badge}
                    </div>
                  )}

                  <h3 className={`text-2xl font-bold mb-1 ${plan.highlight ? 'text-gray-900' : 'text-white'}`}>
                    {plan.name}
                  </h3>
                  <p className={`text-sm mb-6 ${plan.highlight ? 'text-gray-500' : 'text-white/60'}`}>
                    {plan.description}
                  </p>

                  {/* Preço */}
                  <div className="mb-6">
                    {price ? (
                      <>
                        <div className="flex items-baseline gap-1">
                          <span className={`text-4xl font-bold ${plan.highlight ? 'text-purple-600' : 'text-white'}`}>
                            R$ {price.value.toFixed(2).replace('.', ',')}
                          </span>
                          <span className={`text-sm ${plan.highlight ? 'text-gray-400' : 'text-white/50'}`}>{price.label}</span>
                        </div>
                        {price.note && (
                          <p className={`text-xs mt-1 ${plan.highlight ? 'text-gray-400' : 'text-white/50'}`}>{price.note}</p>
                        )}
                      </>
                    ) : (
                      <>
                        <p className={`text-4xl font-bold ${plan.highlight ? 'text-purple-600' : 'text-white'}`}>{plan.priceLabel}</p>
                        <p className={`text-xs mt-1 ${plan.highlight ? 'text-gray-400' : 'text-white/50'}`}>{plan.priceSub}</p>
                      </>
                    )}
                  </div>

                  <ul className="space-y-2.5 mb-8">
                    {plan.features.map((f, i) => (
                      <FeatureItem key={i} text={f} dark={!plan.highlight} />
                    ))}
                  </ul>

                  <Button
                    size="lg"
                    className={`w-full py-5 rounded-xl font-semibold ${
                      plan.highlight
                        ? 'bg-purple-600 hover:bg-purple-700 text-white shadow-lg'
                        : 'bg-white/15 hover:bg-white/25 text-white border border-white/30'
                    }`}
                    onClick={() => navigate('/login')}
                  >
                    {plan.cta}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Card>
              );
            })}
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-8 mt-14">
            {[
              { icon: <Shield className="w-4 h-4" />, label: 'Pagamento seguro' },
              { icon: <Zap className="w-4 h-4" />, label: 'Cancele quando quiser' },
              { icon: <MessageCircle className="w-4 h-4" />, label: 'Suporte humanizado' },
            ].map(({ icon, label }) => (
              <div key={label} className="flex items-center gap-2 text-white/50 text-sm">
                {icon} {label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════
          SEÇÃO 4 — PLANOS EMPRESARIAIS (B2B)
      ══════════════════════════════════════════════════ */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-widest text-purple-600 mb-3">Para empresas</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Inteligência Relacional para <span className="text-purple-600">times</span>
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              A Simby leva cultura relacional para dentro das organizações — com dados, métricas e acompanhamento especializado.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {PLANS_B2B.map((plan) => (
              <Card
                key={plan.id}
                className={`relative p-7 rounded-2xl flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
                  plan.featured
                    ? 'border-2 border-purple-500 shadow-lg bg-white'
                    : 'border border-gray-200 bg-white'
                }`}
              >
                {plan.featured && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-purple-600 text-white text-xs font-semibold px-5 py-1 rounded-full shadow whitespace-nowrap">
                    Mais completo
                  </div>
                )}

                <div className="flex items-center gap-3 mb-1">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${plan.featured ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-600'}`}>
                    {plan.icon}
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-lg leading-none">{plan.name}</p>
                    <p className="text-xs text-gray-400">{plan.subtitle}</p>
                  </div>
                </div>

                <p className="text-xs text-gray-400 mt-3 mb-5 leading-relaxed">{plan.target}</p>

                <div className="mb-5">
                  <p className={`text-2xl font-bold leading-none ${plan.featured ? 'text-purple-600' : 'text-gray-900'}`}>
                    {plan.priceLabel}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">{plan.priceSub}</p>
                  {plan.priceExtra && (
                    <p className="text-xs text-gray-500 mt-1">{plan.priceExtra}</p>
                  )}
                </div>

                <ul className="space-y-2.5 mb-7 flex-1">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${plan.featured ? 'text-purple-500' : 'text-green-500'}`} />
                      <span className="text-sm text-gray-600 leading-relaxed">{f}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  className={`w-full rounded-xl font-semibold ${
                    plan.featured
                      ? 'bg-purple-600 hover:bg-purple-700 text-white'
                      : 'bg-gray-900 hover:bg-gray-800 text-white'
                  }`}
                  onClick={() => window.open('mailto:contato@simby.com.br?subject=' + encodeURIComponent('Interesse: ' + plan.name), '_blank')}
                >
                  {plan.cta}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Card>
            ))}
          </div>

          <p className="text-center text-sm text-gray-400 mt-8">
            Todos os planos empresariais incluem onboarding, suporte dedicado e contrato personalizado.
            <a href="mailto:contato@simby.com.br" className="text-purple-600 hover:underline ml-1">Fale conosco →</a>
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════
          SEÇÃO 5 — DEPOIMENTOS
      ══════════════════════════════════════════════════ */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-3">
              O que dizem quem <span className="text-purple-600">pratica</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                text: 'A Simby transformou a forma como me comunico com minha família. Aprendi a reconhecer minhas necessidades e expressá-las sem julgamentos.',
                author: 'Maria Silva',
                role: 'Mãe e executiva',
              },
              {
                text: 'As simulações com IA são incríveis! Pude praticar conversas difíceis antes de tê-las de verdade no trabalho.',
                author: 'João Santos',
                role: 'Gerente de projetos',
              },
              {
                text: 'Finalmente uma plataforma que ensina inteligência relacional de forma prática, com metodologia sólida e resultado visível.',
                author: 'Ana Costa',
                role: 'Psicóloga',
              },
            ].map((t, i) => (
              <Card key={i} className="bg-gray-50 border border-gray-100 p-6 rounded-2xl shadow-sm hover:shadow-lg transition-shadow">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed text-sm">"{t.text}"</p>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{t.author}</p>
                  <p className="text-xs text-gray-400">{t.role}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════
          SEÇÃO 6 — CTA FINAL
      ══════════════════════════════════════════════════ */}
      <section className="py-24 bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 text-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <img src="/images/simby-mascote.png" alt="A Simby" className="w-28 h-40 object-contain mx-auto mb-8 drop-shadow-2xl" />
          <h2 className="text-4xl md:text-5xl font-bold mb-5">
            Pronta para transformar suas
            <span className="text-pink-300"> relações</span>?
          </h2>
          <p className="text-lg text-white/70 mb-10 max-w-xl mx-auto">
            A Simby está aqui para acompanhar cada passo da sua jornada relacional — com presença, dados e cuidado.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-white text-purple-700 hover:bg-purple-50 px-10 py-6 text-lg rounded-2xl shadow-xl hover:scale-105 transition-all duration-300"
              onClick={() => navigate('/login')}
            >
              Começar gratuitamente
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 px-10 py-6 text-lg rounded-2xl"
              onClick={() => window.open('mailto:contato@simby.com.br', '_blank')}
            >
              Falar com a equipe
            </Button>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════
          FOOTER
      ══════════════════════════════════════════════════ */}
      <footer className="py-10 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-5">
            <img src="/logo-simby.png" alt="A Simby" className="h-9" onError={e => { e.target.style.display = 'none'; }} />
            <div className="flex gap-7 text-gray-400 text-sm">
              <a href="/terms" className="hover:text-purple-600 transition">Termos de Uso</a>
              <a href="/privacy" className="hover:text-purple-600 transition">Privacidade</a>
              <a href="mailto:contato@simby.com.br" className="hover:text-purple-600 transition">Contato</a>
            </div>
            <p className="text-gray-300 text-sm">
              © 2025 A Simby. Todos os direitos reservados. 
              <span className="ml-2 text-gray-400">v4.0</span>
            </p>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default LandingPage;
